'use client';

import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useState, useCallback } from "react";
import { Text } from "../../atoms/Text/Text.js";
import { useId } from "../../utils/hooks/useId.js";
import { useLiveAnnouncement } from "../../utils/hooks/useLiveAnnouncement.js";
import { classNames } from "../../utils/styles/classNames.js";
import { ActionButtons } from "./InlineNotification.ActionButtons.js";
import { Contents } from "./InlineNotification.Contents.js";
import { DismissButton } from "./InlineNotification.DismissButton.js";
import { FocusMockOnHide } from "./utils/FocusMockOnHide.js";
import { processProps } from "./utils/processProps.js";
import { contentPadding, accentBg, start, dismissiblePadding, mainBg, end, center, container } from "./InlineNotification.css.js";
function InlineNotificationComponent(rawProps, ref) {
  const { className, onDismiss: onDismissCallback } = rawProps;
  const props = processProps(rawProps);
  const { label, Icon, variant, dismissible } = props;
  const id = useId();
  const liveId = `${id}-live`;
  const labelId = `${id}-label`;
  const [isDismissed, setIsDismissed] = useState(false);
  const announce = useLiveAnnouncement();
  const onDismiss = useCallback(() => {
    setIsDismissed(true);
    announce(null, { id: liveId });
    onDismissCallback == null ? void 0 : onDismissCallback();
  }, [onDismissCallback]);
  const startClassNames = classNames(
    start,
    accentBg[variant],
    dismissible && contentPadding
  );
  const endClassNames = classNames(
    end,
    mainBg[variant],
    dismissible ? dismissiblePadding : contentPadding
  );
  return /* @__PURE__ */ jsx(FocusMockOnHide, { isHidden: isDismissed, children: /* @__PURE__ */ jsxs(
    "div",
    {
      "aria-labelledby": labelId,
      className: classNames(className, container),
      ref,
      role: "group",
      tabIndex: (
        /* to support "bullet-proof form validation" pattern https://practical-accessibility.today/chapters/form-validation-2/ */
        -1
      ),
      children: [
        /* @__PURE__ */ jsx(Text, { "aria-hidden": "true", id: labelId, visuallyHidden: true, children: label }),
        /* @__PURE__ */ jsx("div", { className: startClassNames, children: /* @__PURE__ */ jsx(Icon, { size: "md" }) }),
        /* @__PURE__ */ jsxs("div", { className: classNames(center, mainBg[variant]), children: [
          /* @__PURE__ */ jsx(Contents, { ...props, liveId }),
          /* @__PURE__ */ jsx(ActionButtons, { ...props })
        ] }),
        /* @__PURE__ */ jsx("div", { className: endClassNames, children: /* @__PURE__ */ jsx(DismissButton, { ...props, onDismiss }) })
      ]
    }
  ) });
}
const InlineNotification = forwardRef(InlineNotificationComponent);
export {
  InlineNotification
};
